// Assume algoliasearch is available globally from a CDN script include.
// If not, it needs to be loaded via a script tag in the HTML.

export class AlgoliaSearchManager {
  constructor(
    appId,
    searchKey,
    indexName,
    inputElementId,
    resultsElementId,
    paginationElementId,
    resultsCountElementId,
    clearFilterElementId,
    initialFilters = null,
    initialSortByIndex = null,
    onSearchTriggered = () => {}, // New callback for search trigger
  ) {
    this.searchClient = algoliasearch(appId, searchKey);
    this.postType = "research"; // Store the post type
    this.currentAlgoliaIndexName = initialSortByIndex || indexName;
    this.index = this.searchClient.initIndex(this.currentAlgoliaIndexName);

    this.onSearchTriggered = onSearchTriggered; // Store the callback

    this.searchInput = document.getElementById(inputElementId);
    this.searchResults = document.getElementById(resultsElementId);
    this.paginationContainer = document.getElementById(paginationElementId);
    this.resultsCountElement = document.getElementById(resultsCountElementId);
    this.clearFilterButton = document.getElementById(clearFilterElementId);

    this.searchTimeout = null;
    this.currentPage = 0;
    this.totalPages = 0;
    this.currentQuery = "";
    this.algoliaFilters = null;
    this.currentFacetFilters = {}; // To store multiple facet filters
    this.isInitialized = false; // Flag to prevent input events during initialization

    // Apply escaping to initial filters
    this.algoliaFilters = initialFilters;

    this.initEventListeners();
    // Note: Initial search is now handled by the calling code (e.g., research-database.js)
    // to avoid race conditions when the search input is populated from URL params
  }

  initEventListeners() {
    if (this.searchInput) {
      this.searchInput.addEventListener("input", (e) => {
        // Ignore input events during initialization to prevent race conditions
        if (!this.isInitialized) {
          return;
        }
        const query = e.target.value.trim();
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.performSearch(query);
          this.showSearchResults();
          this.onSearchTriggered(query); // Call the new callback
        }, 300);
      });

      // Prevent Enter key from submitting/reloading the page
      this.searchInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      });
    }
  }

  performSearch(query, page = 0) {
    this.currentQuery = query;
    this.currentPage = page;
    // Mark as initialized after first explicit search
    this.isInitialized = true;

    // Re-initialize index if the sort index has changed
    if (this.index.indexName !== this.currentAlgoliaIndexName) {
      this.index = this.searchClient.initIndex(this.currentAlgoliaIndexName);
    }

    const searchOptions = {
      hitsPerPage: 20,
      page: page,
    };

    let allFilters = [];

    // Always filter to only include Research post type
    allFilters.push("post_type:Research");

    // Exclude only records explicitly set to false (show records with true or undefined)
    allFilters.push("NOT show_on_research_index:false");

    if (this.algoliaFilters) {
      allFilters.push(this.algoliaFilters);
    }

    for (const attribute in this.currentFacetFilters) {
      if (this.currentFacetFilters[attribute]) {
        allFilters.push(this.currentFacetFilters[attribute]);
      }
    }

    if (allFilters.length > 0) {
      searchOptions.filters = allFilters.join(" AND ");
    }

    this.index
      .search(query, searchOptions)
      .then(({ hits, nbHits, nbPages, page }) => {
        this.totalPages = nbPages;
        this.currentPage = page;
        this.renderResults(hits);
        this.renderPagination(nbPages, page);
        this.updateResultsCount(nbHits, hits.length);
      })
      .catch((err) => {
        console.error("Algolia search error:", err);
        // Simplified error handling: just render empty results on error
        this.renderResults([]);
        this.renderPagination(0, 0);
        this.updateResultsCount(0, query);
      });
  }

  renderResults(hits) {
    if (!this.searchResults) return;

    if (hits.length === 0) {
      this.searchResults.innerHTML = `
        <div class="border-t border-lightborder py-8 text-center text-gray">No content found matching your search</div>
      `;
      return;
    }

    let resultsHtml = this._renderResearchResults(hits);

    this.searchResults.innerHTML = `
      <div class="flex flex-col mt-4 md:-mt-4 border-t border-lightborder md:border-t-0">
        <div class="md:grid-cols-[3fr_1fr_1fr_1fr] gap-x-5 md:gap-8 hidden md:border-b border-lightborder border-b-0 md:grid pb-2.5">
            <div></div>
            <div class="text-sans-sm">Date</div>
            <div class="text-sans-sm">Sub Type</div>
            <div class="text-sans-sm">Fund</div>
        </div>
        ${resultsHtml}
      </div>
    `;
  }

  _renderResearchResults(hits) {
    return hits
      .map((hit) => {
        let date = hit.publication_date || "Date not specified";
        if (typeof date === "number" && !isNaN(date)) {
          const d = new Date(date * 1000);
          date = d.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            timeZone: "America/Los_Angeles", // Ensure consistent display with WordPress timezone
          });
        }
        const title = hit.title || "Title not specified";
        const postUrl = hit.url; // Get the URL of the post
        // Use primary fields with fallback to array first item for backward compatibility
        const contentType =
          hit.primary_content_type ||
          (Array.isArray(hit.content_type) && hit.content_type.length > 0
            ? hit.content_type[0]
            : " ");

        const focusArea =
          hit.primary_focus_area ||
          (Array.isArray(hit["focus-area"]) && hit["focus-area"].length > 0
            ? hit["focus-area"][0]
            : " ");

        const externalIcon = hit.is_external_link
          ? '<span class="whitespace-nowrap decoration-none">&nbsp;<svg class="inline-block" width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.18022 9.96831L9.2283 4.89553L3.81893 10.3049L3.07364 9.55961L8.48301 4.15024L3.3982 4.1863L4.38391 3.20059L10.1779 3.20059L10.1659 8.98261L9.18022 9.96831Z" fill="#333333"/></svg></span>'
          : "";
        const targetAttr = hit.is_external_link ? ' target="_blank"' : "";

        return `
          <div class="md:grid-cols-[3fr_1fr_1fr_1fr] gap-x-5 gap-y-1 md:gap-8 border-b-gray-light before:bg-lightborder relative grid grid-cols-2 border-b-1 transition-colors before:absolute before:-z-1 before:size-full before:scale-y-0 before:transition-transform before:content-[''] before:origin-[var(--origin)]">
            <a href="${postUrl}" class="text-serif-md text-pretty pt-2.5 pb-6 row-span-3 md:row-auto subtle-link inline-block"${targetAttr}>${title}${externalIcon}</a>
            <div class="text-sans-sm pb-0 pt-2 md:py-2.5 col-start-2 md:col-auto">${date}</div>
            <div class="text-sans-sm pb-0 pt-0 md:py-2.5 col-start-2 md:col-auto">${contentType}</div>
            <div class="text-sans-sm pb-2.5 md:py-2.5 col-start-2 md:col-auto">${focusArea}</div>
          </div>
      `;
      })
      .join("");
  }

  renderPagination(totalPages, currentPage) {
    if (!this.paginationContainer) return;

    if (totalPages <= 1) {
      this.paginationContainer.style.display = "none";
      return;
    }

    this.paginationContainer.style.display = "block";

    const maxVisible = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(0, endPage - maxVisible + 1);
    }

    let pagesHtml = "";
    for (let i = startPage; i <= endPage; i++) {
      const activeClass = i === currentPage ? "before:scale-y-100" : "";
      pagesHtml += `
        <li>
          <a class="text-sans-sm w-9 h-12 flex relative before:content-[''] before:absolute before:w-full before:h-[3px] before:top-0 before:left-0 before:scale-y-0 before:origin-top before:transition-all hover:before:scale-y-100 before:bg-black ${activeClass}"
             href="#" data-page="${i}">
            <span class="m-auto">${i + 1}</span>
          </a>
        </li>
      `;
    }

    const prevDisabled =
      currentPage === 0 ? "opacity-50 pointer-events-none" : "";
    const nextDisabled =
      currentPage === totalPages - 1 ? "opacity-50 pointer-events-none" : "";

    this.paginationContainer.innerHTML = `
      <div class="flex flex-col md:flex-row justify-between items-center md:items-start">
        <ul class="flex flex-row flex-nowrap">
          ${pagesHtml}
        </ul>
        <ul class="flex flex-nowrap">
          <li>
            <a class="inline-block relative w-9 h-12 before:content-[url(../assets/chevron.svg)] before:absolute before:top-[50%] before:left-[50%] before:translate-[-50%] before:origin-center before:rotate-90 border-r-1 border-lightborder ${prevDisabled}"
               href="#" data-page="${currentPage - 1}" id="algolia-prev">
              <span class="sr-only">Previous</span>
            </a>
          </li>
          <li>
            <a class="inline-block relative w-9 h-12 before:content-[url(../assets/chevron.svg)] before:absolute before:top-[50%] before:left-[50%] before:translate-[-50%] before:origin-center before:-rotate-90 ${nextDisabled}"
               href="#" data-page="${currentPage + 1}" id="algolia-next">
              <span class="sr-only">Next</span>
            </a>
          </li>
        </ul>
      </div>
    `;

    this.paginationContainer
      .querySelectorAll("a[data-page]")
      .forEach((link) => {
        link.addEventListener("click", (e) => {
          e.preventDefault();
          const page = parseInt(link.dataset.page);
          if (page >= 0 && page < totalPages && page !== currentPage) {
            this.performSearch(this.currentQuery, page);
          }
        });
      });
  }

  updateResultsCount(total = 0, perPage = 0) {
    if (this.resultsCountElement) {
      this.resultsCountElement.textContent = `Showing ${perPage} of ${total}`;
      this.resultsCountElement.style.display = "block";
    }
  }

  showSearchResults() {
    if (this.searchResults) this.searchResults.style.display = "block";
    if (this.paginationContainer && this.totalPages > 1)
      this.paginationContainer.style.display = "block";
  }

  escapeAlgoliaFilterValue(value) {
    if (typeof value !== "string") {
      return value;
    }
    const doc = new DOMParser().parseFromString(value, "text/html");
    const decodedValue = doc.documentElement.textContent;
    const needsQuotes = /[&()![\]|<>=:*_~^?/\\]|\s/.test(decodedValue);
    return needsQuotes ? `"${decodedValue}"` : decodedValue;
  }

  setAlgoliaFilters(filters) {
    if (filters && typeof filters === "string" && filters.includes(":")) {
      const [attribute, value] = filters.split(":", 2);
      this.algoliaFilters = `${attribute}:${this.escapeAlgoliaFilterValue(value)}`;
    } else {
      this.algoliaFilters = filters;
    }
  }

  setAlgoliaFilter(attribute, value) {
    if (value) {
      this.currentFacetFilters[attribute] =
        `${attribute}:${this.escapeAlgoliaFilterValue(value)}`;
    } else {
      delete this.currentFacetFilters[attribute];
    }
  }

  getAlgoliaFacets(attribute) {
    const searchOptions = {
      facets: [attribute],
      hitsPerPage: 0,
    };
    if (this.algoliaFilters) {
      searchOptions.filters = this.algoliaFilters;
    }
    return this.index
      .search("", searchOptions)
      .then(({ facets }) => {
        return facets[attribute] || {};
      })
      .catch((err) => {
        console.error(`Error fetching facets for ${attribute}:`, err);
        return {};
      });
  }

  setAlgoliaSortByIndex(indexName) {
    this.currentAlgoliaIndexName = indexName;
    this.index = this.searchClient.initIndex(this.currentAlgoliaIndexName); // Re-initialize index immediately
  }

  getAlgoliaCurrentQuery() {
    return this.currentQuery;
  }

  setAlgoliaFocusAreaFilter(focusArea) {
    this.setAlgoliaFilter("focus-area", focusArea);
  }

  clear(shouldPerformSearch = true) {
    this.currentQuery = "";
    this.currentPage = 0;
    this.currentPriceRangeFilter = null;
    this.currentFacetFilters = {};
    if (this.searchInput) {
      this.searchInput.value = "";
    }
    if (shouldPerformSearch) {
      this.performSearch("");
    }
  }
}

let algoliaManagerInstance = null;

export function initAlgoliaSearch(...args) {
  if (!algoliaManagerInstance) {
    algoliaManagerInstance = new AlgoliaSearchManager(...args);
  }
}

export function performSearch(...args) {
  algoliaManagerInstance?.performSearch(...args);
}

export function setAlgoliaFilters(...args) {
  algoliaManagerInstance?.setAlgoliaFilters(...args);
}

export function setAlgoliaSortByIndex(...args) {
  algoliaManagerInstance?.setAlgoliaSortByIndex(...args);
}

export function getAlgoliaCurrentQuery(...args) {
  return algoliaManagerInstance?.getAlgoliaCurrentQuery(...args);
}

export function getAlgoliaFacets(...args) {
  return algoliaManagerInstance?.getAlgoliaFacets(...args);
}

export function setAlgoliaFocusAreaFilter(...args) {
  algoliaManagerInstance?.setAlgoliaFocusAreaFilter(...args);
}
